import { Clock } from "lucide-react";
import { useTranslation } from "@/i18n/I18nProvider";
import { isDiscountLive } from "@/hooks/useActiveDiscount";
import type { Lang } from "@/i18n/translations";
import type { Discount } from "@/types/restaurant";
import { ReportLink } from "./ReportLink";

export interface OfferCardProps {
  discount: Discount;
  /** Clock used to decide the live/scheduled pill. */
  now: Date;
  /** Shown above the title so the offer reads standalone. */
  restaurantName?: string;
  /** The venue, for the Report link's subject when no label is shown. */
  venueName?: string;
}

const DAY_ORDER = [1, 2, 3, 4, 5, 6, 0];
const DAY_ABBR: Record<Lang, Record<number, string>> = {
  en: { 1: "Mon", 2: "Tue", 3: "Wed", 4: "Thu", 5: "Fri", 6: "Sat", 0: "Sun" },
  ro: { 1: "Lun", 2: "Mar", 3: "Mie", 4: "Joi", 5: "Vin", 6: "Sâm", 0: "Dum" },
};

/** "Mon · Wed · Fri" style label, or the every-day fallback. */
export function formatOfferDays(days: number[] | null, lang: Lang, everyDay: string): string {
  if (!days || days.length === 0 || days.length === 7) return everyDay;
  const map = DAY_ABBR[lang];
  return DAY_ORDER.filter((d) => days.includes(d))
    .map((d) => map[d])
    .join(" · ");
}

/**
 * Inline offer entry used in the restaurant detail modal: a bigger thumbnail,
 * the title, a live/scheduled pill, and the full description below. Looks clean
 * with or without an `image_url`.
 */
export function OfferCard({ discount, now, restaurantName, venueName }: OfferCardProps) {
  const { t, lang } = useTranslation();
  const live = isDiscountLive(discount, now);
  const hasDescription = Boolean(discount.description?.trim());

  return (
    <div className="rounded-xl border border-white/[0.12] light:border-black/10 bg-zinc-900/50 light:bg-white/60 p-3 shadow-[0_1px_0_rgba(255,255,255,0.05)_inset]">
      <div className="flex gap-3">
        {discount.image_url && (
          <img
            src={discount.image_url}
            alt=""
            className="h-20 w-20 shrink-0 rounded-lg border border-white/10 light:border-black/10 object-cover sm:h-24 sm:w-24"
          />
        )}
        <div className="min-w-0 flex-1">
          {restaurantName && (
            <p className="truncate font-mono text-3xs uppercase tracking-[0.18em] text-zinc-400 light:text-zinc-600">
              {restaurantName}
            </p>
          )}
          <p className="mt-0.5 break-words text-sm font-medium text-zinc-100 light:text-zinc-900">{discount.info}</p>
          <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-2xs text-zinc-400 light:text-zinc-600">
            {live ? (
              <span className="inline-flex items-center gap-1 rounded-full border border-emerald-500/40 bg-emerald-500/15 px-2 py-0.5 font-medium text-emerald-300">
                <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-400" aria-hidden />
                {t("OFFER_LIVE")}
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 rounded-full border border-zinc-800 light:border-black/10 bg-zinc-900/60 light:bg-white/70 px-2 py-0.5">
                <Clock className="h-3 w-3 text-dim light:text-zinc-600" aria-hidden />
                {discount.starts_at}–{discount.ends_at}
              </span>
            )}
            <span className="text-dim light:text-zinc-600">
              {formatOfferDays(discount.days_of_week, lang, t("OFFER_EVERY_DAY"))}
            </span>
          </div>
        </div>
      </div>

      {hasDescription && (
        <p className="mt-3 whitespace-pre-line text-sm leading-relaxed text-zinc-300 light:text-zinc-700">
          {discount.description}
        </p>
      )}

      <div className="mt-2 flex justify-end">
        <ReportLink
          label={`${venueName ?? restaurantName ?? ""}: ${discount.info}`}
          target={{
            kind: "venue",
            restaurantId: discount.restaurant_id,
            restaurantName: venueName ?? restaurantName,
            offerId: discount.id,
            offerInfo: discount.info,
          }}
        />
      </div>
    </div>
  );
}
